import React, { useState, useEffect, useMemo, useRef } from "react";

import SoftBox from "../../components/SoftBox";
import LoadingScreen from "../../components/LoadingScreen";
import { UseCompany } from "../../hooks/company";
import { GET_CHALLENGE_EVENT } from "../../api/challenge";

/* ────────────────────────────────────────────────────────────────────────
   Insight — the right-hand panel of Company View Row 2.
   Tabs: Ask (AI assistant, default) · Challenges · Level ups.
   The Ask view is an on-brand AI assistant (Figma node 1:3610): an animated
   gradient orb, a welcome line, quick-question chips, and an input bar. Answers
   are computed from the SAME live data the rest of Company View already loads
   (leaderboard + metric trend) — no extra request, no placeholder text.
   ──────────────────────────────────────────────────────────────────────── */

/* Recompute a user's points the way the leaderboard does (amt × pointScale /
   pointValue) so "who's leading" matches the Team leaderboard exactly. */
const userPoints = (user, kpiById) => {
  const validKpiIds = Object.keys(kpiById);
  let total = 0;
  Object.keys(user.scores || {}).forEach((key) => {
    if (key === "total") return;
    if (!validKpiIds.includes(key)) return;
    const o = user.scores[key];
    const kpi = kpiById[key];
    const amt = Number(o?.amt) || 0;
    const ps = Number(kpi?.pointScale) || 0;
    const pv = Number(kpi?.pointValue) || 0;
    const ppa = pv > 0 ? ps / pv : ps;
    total += kpi ? amt * ppa : Number(o?.pts) || 0;
  });
  return total;
};

const firstNameOf = (u) => (u?.firstName ? String(u.firstName).split(" ")[0] : "Someone");

const lastNonNull = (arr) => {
  if (!Array.isArray(arr)) return null;
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (arr[i] != null) return arr[i];
  }
  return null;
};

const TAB_ORDER = [
  { key: "ask", label: "Ask" },
  { key: "chal", label: "Challenges" },
  { key: "lvl", label: "Level ups" },
];

const QUICK = [
  { key: "lead", label: "Who's leading this week?" },
  { key: "behind", label: "Which metric is behind?" },
  { key: "track", label: "How are we tracking?" },
];

/* Reveals text character by character for a live-assistant feel. */
const Typewriter = ({ text }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    let i = 0;
    const id = setInterval(() => {
      i += 2;
      setN(i);
      if (i >= text.length) clearInterval(id);
    }, 14);
    return () => clearInterval(id);
  }, [text]);
  return <span>{text.slice(0, n)}</span>;
};

const MiniOrb = () => (
  <span className="ins-orb ins-orb--mini">
    <span className="ins-orb-spin" />
  </span>
);

const Insight = ({ topList = [], metricTrend = null, companyKpis = null }) => {
  const company = UseCompany();
  const companyId = company?._id;

  const [tab, setTab] = useState("ask");
  const [challenge, setChallenge] = useState(undefined); // undefined=loading, null=none, obj=data
  const [answer, setAnswer] = useState(null); // { q, a } | null
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!companyId) return;
    setChallenge(undefined);
    GET_CHALLENGE_EVENT(companyId)
      .then(({ data }) => setChallenge(data || null))
      .catch(() => setChallenge(null));
  }, [companyId]);

  /* Leaderboard, computed once from live props. */
  const leaders = useMemo(() => {
    if (!Array.isArray(topList) || !topList.length) return [];
    return topList
      .filter((u) => !u.isYeti && u.userId)
      .map((u) => {
        const kpiById = {};
        (companyKpis?.[u.userId] || []).forEach((k) => { if (k?._id) kpiById[k._id] = k; });
        return { name: firstNameOf(u), points: Math.round(userPoints(u, kpiById)), active: u.isActive !== false };
      })
      .sort((a, b) => b.points - a.points);
  }, [topList, companyKpis]);

  /* Metric attainment (% of target) across every workspace's metrics. */
  const metricStats = useMemo(() => {
    const all = (metricTrend?.workspaces || []).flatMap((w) => w.metrics || []);
    const withPct = all
      .map((m) => ({ title: m.title, pct: lastNonNull(m.series) }))
      .filter((x) => x.pct != null);
    if (!withPct.length) return null;
    const sorted = [...withPct].sort((a, b) => b.pct - a.pct);
    return {
      total: withPct.length,
      ahead: withPct.filter((x) => x.pct >= 100).length,
      strongest: sorted[0],
      weakest: sorted[sorted.length - 1],
    };
  }, [metricTrend]);

  /* Turn a question into a grounded, coach-style answer from live data. */
  const answerFor = (key) => {
    if (key === "lead") {
      if (!leaders.length) return "No points on the board yet — as the team logs work, I'll tell you who's out front.";
      const top = leaders[0];
      let msg = `${top.name} is out front with ${top.points.toLocaleString()} pts.`;
      if (leaders[1]) {
        const gap = top.points - leaders[1].points;
        msg += ` ${leaders[1].name} is ${gap.toLocaleString()} pts back`;
        msg += gap <= top.points * 0.1 ? " — close enough to flip this week." : ", with room to close the gap.";
      }
      return msg;
    }
    if (key === "behind") {
      if (!metricStats) return "I don't have enough metric history yet to spot what's lagging.";
      const w = metricStats.weakest;
      let msg = `${w.title} is furthest behind at ${Math.round(w.pct)}% of target.`;
      if (metricStats.strongest && metricStats.strongest.title !== w.title) {
        msg += ` ${metricStats.strongest.title} is your strongest at ${Math.round(metricStats.strongest.pct)}% — worth shifting a coaching session toward ${w.title}.`;
      }
      return msg;
    }
    if (key === "track") {
      if (!metricStats) return "Metric tracking will show here once there's a month or two of data.";
      const s = metricStats.strongest;
      return `${metricStats.ahead} of ${metricStats.total} metric${metricStats.total === 1 ? "" : "s"} ${metricStats.ahead === 1 ? "is" : "are"} ahead of target. Strongest is ${s.title} at ${Math.round(s.pct)}%.`;
    }
    return null;
  };

  const ask = (key, label) => setAnswer({ q: label, a: answerFor(key) });

  const submitDraft = () => {
    const q = draft.trim();
    if (!q) return;
    const t = q.toLowerCase();
    let key = null;
    if (/lead|leader|winning|top|out front|ahead of/.test(t)) key = "lead";
    else if (/behind|weak|lag|struggl|low|worst|drop/.test(t)) key = "behind";
    else if (/track|overall|how are we|summary|doing|target/.test(t)) key = "track";
    const a = key
      ? answerFor(key)
      : "I can read your leaderboard and metric performance. Try “who's leading”, “which metric is behind”, or “how are we tracking”.";
    setAnswer({ q, a });
    setDraft("");
  };

  const resetAsk = () => {
    setAnswer(null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  return (
    <SoftBox
      borderRadius="lg"
      sx={{
        p: "16px 20px 16px",
        height: "100%",
        background: "#ffffff",
        border: "1px solid #EAECF0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes insFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
        @keyframes insSpin{to{transform:rotate(360deg)}}
        @keyframes insBreathe{0%,100%{opacity:.5;transform:scale(1)}50%{opacity:.9;transform:scale(1.14)}}
        @keyframes insFade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .ins-orb{position:relative;overflow:hidden;border-radius:50%;
          background:radial-gradient(circle at 33% 27%,#b9d3ff 0%,#6d5cf0 44%,#4f26e6 100%);
          box-shadow:inset 2px 3px 11px rgba(157,255,60,.18),0 6px 16px rgba(79,38,230,.32)}
        .ins-orb-spin{position:absolute;inset:-25%;mix-blend-mode:screen;
          background:conic-gradient(from 0deg,transparent,rgba(155,220,255,.6),transparent 42%);
          animation:insSpin 7s linear infinite}
        .ins-orb--big{width:46px;height:46px;animation:insFloat 6s ease-in-out infinite}
        .ins-orb--mini{width:32px;height:32px;flex:0 0 auto;animation:insFloat 6s ease-in-out infinite}
        .ins-chip{font-family:'DM Sans';font-size:11px;font-weight:500;color:#101828;background:#fff;
          border:1px solid rgba(0,0,0,.10);border-radius:100px;padding:5px 11px;cursor:pointer;white-space:nowrap;
          transition:border-color .15s ease,transform .1s ease}
        .ins-chip:hover{border-color:rgba(0,0,0,.24);transform:translateY(-1px)}
      `}</style>

      <span style={{ fontFamily: "DM Sans", fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em", color: "#0F172A", lineHeight: 1.2 }}>
        Insight
      </span>

      {/* Tabs */}
      <SoftBox sx={{ display: "flex", gap: "2px", mt: "12px", background: "#F4F6F9", borderRadius: "9px", p: "3px" }}>
        {TAB_ORDER.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, height: 32, border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "DM Sans",
              fontSize: 12, fontWeight: tab === t.key ? 600 : 500,
              color: tab === t.key ? "#101828" : "#667085",
              background: tab === t.key ? "#fff" : "transparent",
              boxShadow: tab === t.key ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            {t.label}
          </button>
        ))}
      </SoftBox>

      <SoftBox sx={{ flex: 1, minHeight: 0, mt: "10px", display: "flex", flexDirection: "column" }}>
        {/* ── Ask (AI) ── */}
        {tab === "ask" && (
          <>
            <SoftBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {!answer && (
                <div style={{ animation: "insFade .25s ease", padding: "6px 0 2px" }}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ width: 64, height: 64, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ position: "absolute", inset: -26, borderRadius: "50%", background: "radial-gradient(circle,rgba(128,147,244,.35),transparent 62%)", animation: "insBreathe 4s ease-in-out infinite" }} />
                      <span className="ins-orb ins-orb--big"><span className="ins-orb-spin" /></span>
                    </div>
                  </div>
                  <p style={{ fontFamily: "DM Sans", fontSize: 14, fontWeight: 500, lineHeight: 1.3, color: "#0A0D14", textAlign: "center", letterSpacing: "-0.14px", maxWidth: 200, margin: "12px auto 0" }}>
                    How can I help you grow your team today?
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 14 }}>
                    {QUICK.map((q) => (
                      <button key={q.key} type="button" className="ins-chip" onClick={() => ask(q.key, q.label)}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {answer && (
                <div style={{ animation: "insFade .25s ease" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    <span style={{ background: "#F1F3F9", color: "#344054", fontFamily: "DM Sans", fontSize: 12.5, fontWeight: 500, borderRadius: "12px 12px 2px 12px", padding: "8px 12px", maxWidth: "82%" }}>
                      {answer.q}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <MiniOrb />
                    <div style={{ fontFamily: "DM Sans", fontSize: 12.5, lineHeight: 1.55, color: "#1D2939", paddingTop: 3 }}>
                      <Typewriter text={answer.a} />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetAsk}
                    style={{ marginTop: 14, background: "transparent", border: "none", color: "var(--gmb-primary, #1B598E)", fontFamily: "DM Sans", fontSize: 11.5, fontWeight: 600, cursor: "pointer", padding: 0 }}
                  >
                    ← New question
                  </button>
                </div>
              )}
            </SoftBox>

            {/* Input bar */}
            <div style={{ marginTop: 12, background: "#FAFAFA", border: "1px solid rgba(0,0,0,.07)", boxShadow: "inset 0 0 0 1px #fff", borderRadius: 8, padding: 10 }}>
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitDraft(); }}
                placeholder="Need quick insights…"
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "DM Sans", fontSize: 12, fontWeight: 500, color: "#101828" }}
              />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#B6BDC9" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" strokeLinecap="round" /></svg>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M6 11a6 6 0 0 0 12 0M12 17v4" /></svg>
                </div>
                <button
                  type="button"
                  onClick={submitDraft}
                  aria-label="Send"
                  style={{ width: 30, height: 30, borderRadius: 8, background: "#20232D", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Challenges ── */}
        {tab === "chal" && challenge === undefined && (
          <SoftBox className="h-full w-full flex justify-center items-center">
            <LoadingScreen height="120px" logoHeight="120px" />
          </SoftBox>
        )}
        {tab === "chal" && challenge === null && (
          <EmptyState title="No challenge running" sub="Active challenges will appear here when one starts." />
        )}
        {tab === "chal" && challenge && (
          <SoftBox sx={{ pt: "6px" }}>
            <SoftBox sx={{ border: "1px solid #EEF1F5", borderRadius: "12px", p: "14px 15px", background: "#FBFCFE" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontFamily: "DM Sans", fontSize: 13.5, fontWeight: 600, color: "#101828" }}>
                  {challenge.title || "Company challenge"}
                </span>
                <span style={{ fontFamily: "DM Sans", fontSize: 10, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "#1D7A46", background: "#E7F5EC", borderRadius: 6, padding: "2px 7px" }}>
                  Live
                </span>
              </div>
              <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#667085", marginTop: 6 }}>
                A company-wide competition is underway.
              </div>
            </SoftBox>
          </SoftBox>
        )}

        {/* ── Level ups ── */}
        {tab === "lvl" && (
          <EmptyState title="Level-ups coming here" sub="As players climb levels, the latest ones will show up. Needs one small endpoint to go live." />
        )}
      </SoftBox>
    </SoftBox>
  );
};

const EmptyState = ({ title, sub }) => (
  <SoftBox sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", px: "10px", py: "24px" }}>
    <span style={{ width: 34, height: 34, borderRadius: "50%", border: "1.5px solid #E4E7EC", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#D0D5DD" }} />
    </span>
    <span style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#344054" }}>{title}</span>
    <span style={{ fontFamily: "DM Sans", fontSize: 11.5, color: "#98A2B3", marginTop: 4, lineHeight: 1.5, maxWidth: 220 }}>{sub}</span>
  </SoftBox>
);

export default Insight;
